/**
 * @fileoverview Invoice Document table component
 * Displays invoices synchronized from SAP
 */
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { intl, DmsButtonGroup, DmsButton, havePermissions } from "dmsbasereact";
import { Chip, Box, Link } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import { DmsEntityTable } from "../DmsEntityTable";
import { InvoiceDocumentStateEnum } from "../../constants/InvoiceDocument";
import { Permissions } from "../../constants/Permissions";

const VISIBLE_ITEMS = 1;

const CollapsibleList = ({ items }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return "-";
  if (items.length <= VISIBLE_ITEMS) return items.join(", ");

  const visible = expanded ? items : items.slice(0, VISIBLE_ITEMS);
  const remaining = items.length - VISIBLE_ITEMS;

  return (
    <Box>
      {visible.join(", ")}
      <Link
        component="button"
        variant="caption"
        sx={{ ml: 0.5, verticalAlign: 'baseline' }}
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
      >
        {expanded
          ? intl.get("ShowLess").d("Show less")
          : `+${remaining}`}
      </Link>
    </Box>
  );
};

/**
 * Invoice Document table component using DmsEntityTable
 * @param {Function} onViewDetails - Callback when viewing invoice details
 * @param {Function} onCancel - Callback when cancelling an invoice
 */
const InvoiceDocumentTable = forwardRef(({ onViewDetails = null, onCancel = null, url = "/invoice_document/table", readOnlyLabel = undefined, createdByLabel = undefined }, ref) => {
  // SYSTEM_SETTINGS_PAGE is granted only to EUROFARMA_GESTAO_LENA, so it works
  // as a gate for "is the requester Gestão Lena". The role name itself is NOT
  // a valid permission scope — havePermissions checks scopes, not role names.
  const isGestaoLena = havePermissions([Permissions.SYSTEM_SETTINGS_PAGE]);
  const resolvedCreatedByLabel = createdByLabel || intl.get("CreatedBy").d("Created by");

  const columns = useMemo(() => [
    {
      label: intl.get("InvoiceNumber").d("Invoice Number"),
      column_name: "danfe_number",
      filter: true,
      width: "150px",
      element: (row) => (!row.danfe_number || row.danfe_number === "UNKNOWN") ? "N/A" : row.danfe_number,
    },
    {
      label: intl.get("InvoiceType").d("Type"),
      column_name: "invoice_type",
      width: "100px",
      element: (row) => {
        const invoiceType = row.relative_type != null ? row.relative_type
          : (typeof row.invoice_type === 'object' ? row.invoice_type.value : row.invoice_type);
        const typeMap = {
          0: { label: "Inbound", color: "info" },
          1: { label: "Outbound", color: "success" },
          2: { label: "Shipping", color: "warning" },
        };
        const type = typeMap[invoiceType] || { label: "Unknown", color: "default" };
        return (
          <Chip
            label={intl.get(type.label).d(type.label)}
            size="small"
            color={type.color}
            variant="outlined"
          />
        );
      },
    },
    {
      label: intl.get("OwnerEntity").d("Owner Entity"),
      column_name: "owner_entity",
      filter: true,
      width: "200px",
      element: (row) => row.owner_entity?.name || "-",
    },
    {
      label: intl.get("DestinationEntity").d("Destination Entity"),
      column_name: "destination_entity",
      filter: true,
      width: "200px",
      element: (row) => row.destination_entity?.name || "-",
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "invoice_state",
      width: "120px",
      element: (row) => {
        const invoiceState = typeof row.invoice_state === 'object' ? row.invoice_state.value : row.invoice_state;
        const getStateDisplay = () => {
          if (invoiceState === 0) return { label: "Created", color: "default" };
          if (invoiceState === 1) return { label: "InProgress", color: "info" };
          if (invoiceState === 2) {
            return { label: "Received", color: "success" };
          }
          if (invoiceState === 3) return { label: "Cancelled", color: "error" };
          if (invoiceState === 4) return { label: "Devolution", color: "warning" };
          if (invoiceState === 5) return { label: "Sent", color: "info" };
          return { label: "Unknown", color: "default" };
        };

        const state = getStateDisplay();
        return (
          <Chip
            label={intl.get(state.label).d(state.label)}
            size="small"
            color={state.color}
          />
        );
      },
    },
    {
      label: intl.get("Items").d("Items"),
      column_name: "invoice_document_items",
      width: "80px",
      sortable: false,
      element: (row) => (
        <Chip
          label={row.invoice_document_items?.length || 0}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      label: intl.get("ProductDescription").d("Product Description"),
      column_name: "product_description",
      width: "200px",
      sortable: false,
      element: (row) => {
        const descriptions = row.invoice_document_items
          ?.map(item => item.batch?.part?.description)
          .filter(Boolean) || [];
        return <CollapsibleList items={descriptions} />;
      },
    },
    {
      label: intl.get("Quantity").d("Quantity"),
      column_name: "quantity",
      width: "100px",
      sortable: false,
      element: (row) => {
        const quantities = row.invoice_document_items
          ?.map(item => item.quantity)
          .filter(q => q != null)
          .map(String) || [];
        return <CollapsibleList items={quantities} />;
      },
    },
    {
      label: intl.get("BatchNumber").d("Batch"),
      column_name: "batch_number",
      width: "250px",
      sortable: false,
      element: (row) => {
        const batches = row.invoice_document_items
          ?.map(item => item.batch?.number)
          .filter(Boolean) || [];
        return <CollapsibleList items={batches} />;
      },
    },
    {
      label: intl.get("Expiration").d("Expiration"),
      column_name: "expiration",
      width: "120px",
      sortable: false,
      element: (row) => {
        const expirations = row.invoice_document_items
          ?.map(item => {
            if (!item.batch?.expiration) return null;
            const date = new Date(item.batch.expiration);
            return date.toLocaleDateString('pt-BR');
          })
          .filter(Boolean) || [];
        return <CollapsibleList items={expirations} />;
      },
    },
    ...(isGestaoLena ? [{
      label: resolvedCreatedByLabel,
      column_name: "created_by_display",
      width: "220px",
      sortable: false,
      filter: false,
      element: (row) => row.created_by_display || "-",
    }] : []),
  ], [isGestaoLena, resolvedCreatedByLabel]);

  const actionsRenderer = useCallback(({ row, onView }) => {
    const invoiceState = typeof row.invoice_state === 'object' ? row.invoice_state.value : row.invoice_state;
    const canCancel = invoiceState === InvoiceDocumentStateEnum.CREATED ||
                      invoiceState === InvoiceDocumentStateEnum.SHIPPING;

    return (
      <DmsButtonGroup>
        {onView && (
          <DmsButton
            icon={<VisibilityIcon />}
            title={intl.get("ViewDetails").d("View Details")}
            onClick={() => onView(row)}
          />
        )}
        {canCancel && onCancel && (
          <DmsButton
            icon={<CancelIcon />}
            title={intl.get("CancelInvoice").d("Cancel Invoice")}
            onClick={(e) => {
              e.stopPropagation();
              onCancel(row);
            }}
            variant="danger"
          />
        )}
      </DmsButtonGroup>
    );
  }, [onCancel]);

  const handleViewDetails = useCallback((row) => {
    if (onViewDetails) {
      onViewDetails(row);
    }
  }, [onViewDetails]);

  return (
    <DmsEntityTable
      ref={ref}
      url={url}
      columns={columns}
      onView={handleViewDetails}
      actionsRenderer={actionsRenderer}
      actionsWidth="120px"
      hideExport={true}
      createButtonLabel={readOnlyLabel !== undefined ? readOnlyLabel : intl.get("InvoicesReadOnly").d("Data synchronized from SAP (read-only)")}
    />
  );
});

InvoiceDocumentTable.displayName = "InvoiceDocumentTable";

InvoiceDocumentTable.propTypes = {
  onViewDetails: PropTypes.func,
  onCancel: PropTypes.func,
  url: PropTypes.string,
  readOnlyLabel: PropTypes.string,
  createdByLabel: PropTypes.string,
};


export { InvoiceDocumentTable };



